import { useEffect, useRef, useState } from "react";
import {
  adminListAIJobs, adminTriggerAIJob, adminBulkSeedAIJob,
  adminAIJobLogs, adminUpdateAIJob, adminGetAIDataModel,
  formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Database, ArrowsClockwise, Robot, Play, Lightning,
  ListBullets, Clock, CheckCircle, XCircle, Minus,
  CaretDown, CaretUp, Spinner, Warning, ToggleLeft, ToggleRight,
} from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";

// ── Constants ────────────────────────────────────────────────────────────────

const SECTION_EMOJI = {
  news: "📰", models: "🤖", agents: "🕹️", research: "🔬",
  companies: "🏢", startups: "🚀", funding: "💰", coding: "💻",
  tools: "🛠️", events: "🗓️", learning: "📚", benchmarks: "📊",
  marketplace: "🛒", dashboard: "📋",
};

const SECTION_LABEL = {
  news: "AI News", models: "AI Models", agents: "AI Agents", research: "AI Research",
  companies: "AI Companies", startups: "AI Startups", funding: "AI Funding", coding: "AI Coding",
  tools: "AI Tools", events: "AI Events", learning: "AI Learning", benchmarks: "Benchmarks",
  marketplace: "Marketplace", dashboard: "Dashboard",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function cronHuman(cron) {
  if (!cron) return "—";
  const parts = cron.split(" ");
  if (parts.length !== 5) return cron;
  const [min, hour] = parts;
  if (hour.startsWith("*/")) {
    const interval = parseInt(hour.slice(2), 10);
    const times = [];
    for (let h = 0; h < 24; h += interval) {
      const istTotal = h * 60 + 30 + 5 * 60;
      const istH = Math.floor(istTotal / 60) % 24;
      times.push(`${String(istH).padStart(2, "0")}:30`);
    }
    return `Every ${interval}h IST (${times.join(", ")})`;
  }
  const utcH = parseInt(hour, 10);
  const utcM = parseInt(min, 10);
  const totalMinutes = utcH * 60 + utcM + 5 * 60 + 30;
  const istH = Math.floor(totalMinutes / 60) % 24;
  const istM = totalMinutes % 60;
  return `Daily ${String(istH).padStart(2, "0")}:${String(istM).padStart(2, "0")} IST`;
}

function relativeTime(iso) {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function JobStatusBadge({ status }) {
  if (status === "running") {
    return (
      <AdminBadge variant="running">
        <Spinner size={10} className="animate-spin" /> Running
      </AdminBadge>
    );
  }
  if (status === "error") {
    return (
      <AdminBadge variant="error">
        <XCircle size={10} weight="fill" /> Error
      </AdminBadge>
    );
  }
  return (
    <AdminBadge variant="idle">
      <Minus size={10} /> Idle
    </AdminBadge>
  );
}

function LogsPanel({ section, onClose }) {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    adminAIJobLogs(section, 10)
      .then((d) => { if (alive) setLogs(Array.isArray(d) ? d : (d?.items ?? [])); })
      .catch(() => { if (alive) setLogs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [section]);

  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last 10 Runs</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
          <CaretUp size={12} />
        </button>
      </div>
      {loading ? (
        <p className="text-slate-400 text-xs">Loading…</p>
      ) : !logs?.length ? (
        <p className="text-slate-400 text-xs">No runs yet.</p>
      ) : (
        <div className="space-y-1.5">
          {logs.map((log, i) => (
            <div key={i} className="flex items-start gap-2 text-xs font-mono">
              <span className="shrink-0 mt-0.5">
                {log.status === "success"
                  ? <CheckCircle size={10} weight="fill" className="text-emerald-500" />
                  : <XCircle size={10} weight="fill" className="text-red-500" />}
              </span>
              <span className="text-slate-400 shrink-0 w-14">{relativeTime(log.started_at)}</span>
              <span className="text-slate-600 truncate flex-1">{log.topic || "—"}</span>
              <span className="text-slate-400 shrink-0">
                {log.status === "success" ? `${log.items_fetched ?? 0}→${log.items_upserted ?? 0}` : "err"}
              </span>
              <span className="text-slate-300 shrink-0">
                {log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
      {logs?.find((l) => l.status === "error") && (
        <p className="mt-2 text-red-600 text-xs truncate">
          {logs.find((l) => l.status === "error")?.error}
        </p>
      )}
    </div>
  );
}

function BulkSeedPanel({ section, onClose }) {
  const [runs, setRuns] = useState(7);
  const [delay, setDelay] = useState(5);
  const [busy, setBusy] = useState(false);

  const start = () => {
    setBusy(true);
    adminBulkSeedAIJob(section, runs, delay)
      .then(() => {
        toast.success(`Bulk seed started: ${runs} runs for ${SECTION_LABEL[section] || section}`);
        onClose();
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };

  const estTime = runs * delay;
  const estRecords = section === "news" ? runs * 40
    : ["events", "learning", "marketplace", "tools"].includes(section) ? runs * 30
    : runs * 25;

  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bulk Seed</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
          <CaretUp size={12} />
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-xs text-slate-500">
          Runs
          <input
            type="number" min={1} max={50} value={runs}
            onChange={(e) => setRuns(Math.min(50, Math.max(1, +e.target.value)))}
            className="w-14 bg-white border border-slate-300 rounded-lg px-2 py-1 text-slate-900 text-xs text-center focus:outline-none focus:border-indigo-500"
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-500">
          Delay (s)
          <input
            type="number" min={3} max={60} value={delay}
            onChange={(e) => setDelay(Math.min(60, Math.max(3, +e.target.value)))}
            className="w-14 bg-white border border-slate-300 rounded-lg px-2 py-1 text-slate-900 text-xs text-center focus:outline-none focus:border-indigo-500"
          />
        </label>
        <button
          onClick={() => { if (!busy) start(); }} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          {busy ? <Spinner size={10} className="animate-spin" /> : <Lightning size={10} weight="fill" />}
          Start Seed
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-400">Est. ~{estTime}s total · ~{estRecords} records</p>
    </div>
  );
}

function SectionJobCard({ job, onRefresh }) {
  const [running, setRunning] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [toggling, setToggling] = useState(false);

  const section = job.section;
  const enabled = job.enabled !== false;
  const status = running ? "running" : job.status;

  const runNow = () => {
    if (running) return;
    setRunning(true);
    adminTriggerAIJob(section)
      .then(() => {
        toast.success(`${SECTION_LABEL[section] || section} job triggered`);
        setTimeout(() => { setRunning(false); onRefresh(); }, 8000);
      })
      .catch((e) => { toast.error(formatApiError(e)); setRunning(false); });
  };

  const toggleEnabled = () => {
    setToggling(true);
    adminUpdateAIJob(section, { enabled: !enabled })
      .then(() => {
        toast.success(`${SECTION_LABEL[section] || section} ${!enabled ? "enabled" : "disabled"}`);
        onRefresh();
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setToggling(false));
  };

  return (
    <div className={`bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-col gap-3 transition-opacity ${!enabled ? "opacity-50" : ""}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg leading-none">{SECTION_EMOJI[section] || "🔲"}</span>
          <span className="text-sm font-semibold text-slate-900 truncate">
            {SECTION_LABEL[section] || section}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <JobStatusBadge status={status} />
          <button
            onClick={toggleEnabled} disabled={toggling}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-40"
            title={enabled ? "Disable job" : "Enable job"}
          >
            {enabled
              ? <ToggleRight size={18} weight="fill" className="text-emerald-500" />
              : <ToggleLeft size={18} className="text-slate-300" />}
          </button>
        </div>
      </div>

      {/* Schedule + stats */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-slate-400 text-xs">
          <Clock size={10} />
          <span>{cronHuman(job.cron)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-400 text-xs">
          <ArrowsClockwise size={10} />
          <span>
            Last: <span className="text-slate-600">{relativeTime(job.last_run)}</span>
            {" · "}Runs: <span className="text-slate-600">{job.run_count ?? 0}</span>
            {" · "}Fetched: <span className="text-slate-600">{job.item_count ?? 0}</span>
          </span>
        </div>
        {job.last_error && (
          <div className="flex items-start gap-1.5 text-red-600 text-xs">
            <Warning size={10} className="mt-0.5 shrink-0" />
            <span className="truncate">{job.last_error}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={runNow} disabled={running || !enabled}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 transition-colors disabled:opacity-40"
        >
          {running ? <Spinner size={10} className="animate-spin" /> : <Play size={10} weight="fill" />}
          Run Now
        </button>
        <button
          onClick={() => { setShowBulk((v) => !v); setShowLogs(false); }}
          disabled={!enabled}
          className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors disabled:opacity-40 ${
            showBulk
              ? "bg-indigo-50 border-indigo-200 text-indigo-700"
              : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-600"
          }`}
        >
          <Lightning size={10} weight="fill" />
          Bulk Seed
          {showBulk ? <CaretUp size={9} /> : <CaretDown size={9} />}
        </button>
        <button
          onClick={() => { setShowLogs((v) => !v); setShowBulk(false); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors ${
            showLogs
              ? "bg-slate-100 border-slate-300 text-slate-900"
              : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-600"
          }`}
        >
          <ListBullets size={10} />
          Logs
          {showLogs ? <CaretUp size={9} /> : <CaretDown size={9} />}
        </button>
      </div>

      {showBulk && <BulkSeedPanel section={section} onClose={() => setShowBulk(false)} />}
      {showLogs && <LogsPanel section={section} onClose={() => setShowLogs(false)} />}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AIJobsTab() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modelConfig, setModelConfig] = useState(null);
  const intervalRef = useRef(null);

  const load = () => {
    adminListAIJobs()
      .then((d) => { setJobs(d); setErr(""); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    adminGetAIDataModel().then(setModelConfig).catch(() => {});
    return () => clearInterval(intervalRef.current);
  }, []);

  const totalFetched = jobs.reduce((s, j) => s + (j.item_count ?? 0), 0);
  const running = jobs.filter((j) => j.status === "running").length;
  const errored = jobs.filter((j) => j.status === "error").length;
  const lastActivity = jobs.map((j) => j.last_run).filter(Boolean).sort().at(-1);

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;
  if (err) return <p className="text-red-600 text-sm">{err}</p>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Database size={14} className="text-slate-500" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              AI Data Pipeline · {jobs.length} sections
            </span>
          </div>
          <p className="text-sm text-slate-500 max-w-lg">
            Jobs run automatically on schedule. Use "Run Now" for an immediate fetch or "Bulk Seed" to rapidly populate a collection to 100+ records.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-600 transition-colors shrink-0"
        >
          <ArrowsClockwise size={13} /> Refresh
        </button>
      </div>

      {/* Active model strip */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm flex-wrap">
        <Robot size={14} className="text-slate-400 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">AI Data Model:</span>
        {modelConfig ? (
          modelConfig.using_fallback ? (
            <AdminBadge variant="warning">Emergent fallback (budget key)</AdminBadge>
          ) : (
            <AdminBadge variant={modelConfig.has_key ? "success" : "error"}>
              {modelConfig.model_name} · {modelConfig.provider}
              {!modelConfig.has_key && " — no API key"}
            </AdminBadge>
          )
        ) : (
          <span className="text-xs text-slate-400">loading…</span>
        )}
        <span className="ml-auto text-xs text-slate-400">
          Configure in <strong className="text-slate-600">Models &amp; Keys</strong> tab
        </span>
      </div>

      {/* Summary stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Sections",  value: jobs.length,                    accent: null },
          { label: "Running Now",     value: running,                        accent: running > 0 ? "text-amber-600" : null },
          { label: "Total Fetched",   value: totalFetched.toLocaleString(),  accent: null },
          { label: "Last Activity",   value: relativeTime(lastActivity),     accent: errored > 0 ? "text-red-600" : null },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{s.label}</div>
            <div className={`text-3xl font-bold ${s.accent ?? "text-slate-900"}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Schedule reference table */}
      <AdminCard header="Schedule Reference">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {["Section", "Schedule", "Status", "Last Run", "Fetched"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.section} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-700 text-sm">
                    {SECTION_EMOJI[job.section]} {SECTION_LABEL[job.section] || job.section}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{cronHuman(job.cron)}</td>
                  <td className="px-4 py-2.5"><JobStatusBadge status={job.status} /></td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{relativeTime(job.last_run)}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-sm font-medium">{(job.item_count ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      {/* Per-section cards */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Section Controls</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <SectionJobCard key={job.section} job={job} onRefresh={load} />
          ))}
        </div>
      </div>
    </div>
  );
}
